import { useEffect, useState } from "react";
import "./AppEmployeeRecordPayrollEmployee.css";
import { 
    CheckEmployee,
    EmployeeRecord,
    Company,
    CheckBankAccount,
    CheckWorkplace,
    Address,
    getCurrentCheckCompanyId,
    convertDateToCheckYYYYMMDD,
    CheckBankAccountStatus,
    CHECK_BANK_ACCOUNT_STATUS,
    CheckResponseError, 
    convertTextareaToAddress,
    CheckForm,
    CheckFormStatus,
    isValidSSN,
    formatSSN,
    isCheckError
} from "winslow-core";
import { Alert, AlertDescription, Button, Input, Label } from "../../../ds2/components";
import { AppCheckEmployeeStatus } from "../app-payroll/AppCheckEmployeeStatus";
import { AppPayBankAccount } from "../app-payroll/AppPayBankAccount";
import { 
    apiCheckGetEmployee, 
    apiCheckCreateBankAccount,
    apiCheckListEmployeeBankAccounts,
    apiCheckCreateWorkplace,
    apiCheckCreateEmployee,
    apiCheckListEmployeeForms,
    apiCheckGetForm,
    apiCheckUpdateEmployee,
    apiCheckUpdateEmployeeSSN,
    apiCheckSubmitEmployeeForm
} from "../../../services/PayrollService";
import { Plus } from "lucide-react";
import { ActionLink } from "../../../ds2/components/ActionLink";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Icon } from "../../../ds2/components";
import { Edit } from "lucide-react";
import "./AppEmployeeRecordPayrollEmployee.css";
import "../app-payroll/AppPayBankAccounts.css";
import { apiGetCompanyByWorkspaceId } from "../../../services/CompanyService";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { erGetCPValue } from "./employeeRecordUtils";
import { apiUpdateEmployeeRecord } from "../../../services/EmployeeRecordService";
import { AppCheckErrorMessage } from "../app-payroll/AppCheckErrorMessage";
import { AppPayEmployeeOnboardingStatus } from "../app-payroll/AppPayEmployeeOnboardingStatus";
import { ModalComponentWrapper } from "../../shared/ModalComponentWrapper";
import { AppEmployeeRecordCheckForm } from "./AppEmployeeRecordCheckForm";

type AppEmployeeRecordPayrollEmployeeProps = {
    employeeRecord: EmployeeRecord | null;
    checkEmployee: CheckEmployee | null;
    onCheckEmployeeCreated?: (checkEmployee: CheckEmployee) => void;
    onCheckEmployeeUpdated?: () => void;
};

export const AppEmployeeRecordPayrollEmployee = ({ 
    employeeRecord,
    checkEmployee,
    onCheckEmployeeCreated,
    onCheckEmployeeUpdated
}: AppEmployeeRecordPayrollEmployeeProps) => {
    const storeUser = useSelector(selectUser);

    const [showBankAccountForm, setShowBankAccountForm] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState<boolean>(false);

    const [ssn, setSSN] = useState<string>("");
    const [bankAccounts, setBankAccounts] = useState<CheckBankAccount[]>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<string | null>(null);
    const [editingBankAccount, setEditingBankAccount] = useState<CheckBankAccount | null>(null);

    const [createdCheckEmployeeId, setCreatedCheckEmployeeId] = useState<string | null>(null);

    const [residence, setResidence] = useState<Address | null>(null);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [originalSSN, setOriginalSSN] = useState<string>("");
    const [hasInitializedSSN, setHasInitializedSSN] = useState<boolean>(false);

    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);
    const [showOnboardingStatusModal, setShowOnboardingStatusModal] = useState<boolean>(false);
    const [checkFormStatuses, setCheckFormStatuses] = useState<CheckFormStatus[]>([]);
    const [loadingForms, setLoadingForms] = useState<boolean>(false);
    const [submittingForm, setSubmittingForm] = useState<boolean>(false);
    const [selectedFormStatus, setSelectedFormStatus] = useState<CheckFormStatus | null>(null);
    const [selectedForm, setSelectedForm] = useState<CheckForm | null>(null);
    const [showFormModal, setShowFormModal] = useState<boolean>(false);
    const [loadingFormDetails, setLoadingFormDetails] = useState<boolean>(false);
    const [ssnInputFocused, setSSNInputFocused] = useState<boolean>(false);

    

    useEffect(() => {
        if (employeeRecord) {
            const cpLongText = erGetCPValue(employeeRecord, "system-address") || "";
            const convertedAddress = cpLongText ? convertTextareaToAddress(cpLongText) : null;
            setResidence(convertedAddress);
        }
    }, [employeeRecord]);

    useEffect(() => {
        if (checkEmployee?.id) {
            loadBankAccounts();
            // Reset changes when checkEmployee loads
            setHasChanges(false);
            // Store current SSN as original when checkEmployee loads (only once)
            if (!hasInitializedSSN) {
                setOriginalSSN(ssn || "");
                setHasInitializedSSN(true);
            }
        } else {
            // Reset when checkEmployee is cleared
            setHasChanges(false);
            setOriginalSSN("");
            setHasInitializedSSN(false);
        }
    }, [checkEmployee?.id]);

    // Initialize SSN baseline when checkEmployee first loads
    useEffect(() => {
        if (checkEmployee?.id && !hasInitializedSSN) {
            setOriginalSSN(ssn || "");
            setHasInitializedSSN(true);
        }
    }, [checkEmployee?.id, ssn, hasInitializedSSN]);

    // Load forms if checkEmployee has withholdings
    useEffect(() => {
        (async () => {
            if (checkEmployee?.id && checkEmployee.onboard?.withholdings && checkEmployee.onboard.withholdings.length > 0) {
                await loadForms();
            }
        })();
    }, [checkEmployee?.id, checkEmployee?.onboard?.withholdings]);

    const loadForms = async () => {
        if (!checkEmployee?.id) {
            return;
        }

        setLoadingForms(true);
        
        // get the high level forms summary
        const apiResponse = await apiCheckListEmployeeForms(checkEmployee.id);
        const formStatuses = apiResponse?.data;
        setCheckResponseError(apiResponse?.checkResponseError);

        if (!apiResponse || isCheckError(apiResponse) || !formStatuses) {
            toast("Error loading forms", toastFail);
            setLoadingForms(false);
            setCheckFormStatuses([]);
            return;
        }

        setCheckFormStatuses(formStatuses);
        setLoadingForms(false);
    };

    const handleOpenForm = async (formStatus: CheckFormStatus) => {
        setSelectedFormStatus(formStatus);
        setLoadingFormDetails(true);
        setShowFormModal(true);

        try {
            // Load the full form details with parameters
            const apiResponse = await apiCheckGetForm(checkEmployee!.id, formStatus.form.id);
            const theForm = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !theForm) {
                toast("Error loading form details", toastFail);
                setShowFormModal(false);
                return;
            }

            setSelectedForm(theForm);
        } catch (error) {
            console.error("[AppEmployeeRecordPayrollEmployee] Error loading form details:", error);
            toast("Error loading form details", toastFail);
            setShowFormModal(false);
        } finally {
            setLoadingFormDetails(false);
        }
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
        setSelectedForm(null);
        setSelectedFormStatus(null);
    };

    const formatDateTime = (dateString: string): string => {
        if (!dateString) return "";
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    // Helper functions for bank account display
    const isPlaidType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { plaid_bank_account: any }> => {
        return "plaid_bank_account" in account;
    };

    const isRawType = (account: CheckBankAccount): account is Extract<CheckBankAccount, { raw_bank_account: any }> => {
        return "raw_bank_account" in account;
    };

    const getInstitutionName = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return account.plaid_bank_account.institution_name || "-";
        } else if (isRawType(account)) {
            return account.raw_bank_account.institution_name || "-";
        }
        return "-";
    };



    const getAccountNumber = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return `****${account.plaid_bank_account.mask}`;
        } else if (isRawType(account)) {
            return `****${account.raw_bank_account.account_number_last_four}`;
        }
        return "-";
    };

    const getSubtype = (account: CheckBankAccount): string => {
        if (isPlaidType(account)) {
            return account.plaid_bank_account.subtype.charAt(0).toUpperCase() + account.plaid_bank_account.subtype.slice(1);
        } else if (isRawType(account)) {
            return account.raw_bank_account.subtype.charAt(0).toUpperCase() + account.raw_bank_account.subtype.slice(1);
        }
        return "-";
    };

    

    const formatStatus = (status: CheckBankAccountStatus): string => {
        const statusOptions: GenericSelectOption[] = CHECK_BANK_ACCOUNT_STATUS.map((status) => ({
            displayName: status.split("_").map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(" "),
            value: status,
        }));
        const option = statusOptions.find(opt => opt.value === status);
        return option?.displayName || status;
    };

    const loadBankAccounts = async () => {
        if (!checkEmployee?.id) return;

        setLoadingBankAccounts(true);
        try {
            const apiResponse = await apiCheckListEmployeeBankAccounts(checkEmployee.id);
            const accounts = apiResponse?.data;

            if (!apiResponse || isCheckError(apiResponse) || !accounts) {
                toast("Error loading bank accounts", toastFail);
                setLoadingBankAccounts(false);
                return;
            }

            setBankAccounts(accounts || []);
            
            // If only one bank account, default to it
            if (accounts.length === 1) {
                setSelectedBankAccountId(accounts[0].id);
            }
        } catch (error) {
            console.error("[AppEmployeeRecordPayrollEmployee] Error loading bank accounts:", error);
            toast("Error loading bank accounts", toastFail);
        } finally {
            setLoadingBankAccounts(false);
        }
    };


    const handleCreateCheckBankAccount = async (createdBankAccount: CheckBankAccount) => {
        // Bank account was already created by AppPayBankAccount component
        // Reload bank accounts and check employee data
        await loadBankAccounts();
        
        if (employeeRecord?.id) {
            const apiResponse = await apiCheckGetEmployee(employeeRecord.id);
            if (apiResponse?.data && onCheckEmployeeCreated) {
                onCheckEmployeeCreated(apiResponse.data);
            }
        }
        
        setShowBankAccountForm(false);
        setEditingBankAccount(null);
        
        // Mark as changed if checkEmployee exists (update scenario)
        if (checkEmployee) {
            setHasChanges(true);
        }
    };

    const handleUpdateCheckBankAccount = async (updatedBankAccount: CheckBankAccount) => {
        // Bank account was already updated by AppPayBankAccount component
        // Reload bank accounts
        await loadBankAccounts();
        setEditingBankAccount(null);
        
        // Mark as changed
        setHasChanges(true);
    };

    const handleEditClick = (bankAccount: CheckBankAccount) => {
        // Only allow editing raw bank accounts
        if (!isRawType(bankAccount)) {
            return;
        }
        setEditingBankAccount(bankAccount);
        setShowBankAccountForm(true);
    };

    const handleCreateCheckEmployee = async () => {
        if (!employeeRecord) {
            toast("Employee record not found", toastFail);
            return;
        }

        // // Validate SSN (9 digits, with or without dashes)
        // const ssnDigits = ssn.replace(/\D/g, "");
        // if (ssnDigits.length !== 9) {
        //     toast("SSN must be exactly 9 digits", toastFail);
        //     return;
        // }

        // get check company
        const checkCompanyId = getCurrentCheckCompanyId(storeUser);
        if (!checkCompanyId) {
            toast("Extended company information not found", toastFail);
            return;
        }

        if (!residence) {
            toast("Residence address is required", toastFail);
            return;
        }

        // make sure there is a start date
        const startDate = erGetCPValue(employeeRecord, "system-startDate") || "";
        if (!startDate) {
            toast("Start date is required", toastFail);
            return;
        }

        const birthDate = erGetCPValue(employeeRecord, "system-birthDate") || "";
        if (!birthDate) {
            toast("Birth date is required", toastFail);
            return;
        }

         // First, get employee data from employeeRecord
        const firstName = erGetCPValue(employeeRecord, "system-firstName") || "";
        const lastName = erGetCPValue(employeeRecord, "system-lastName") || "";
        const middleName = erGetCPValue(employeeRecord, "system-middleName") || "";

        if (!lastName) {
            toast("Last name is required", toastFail);
            return;
        }

        setCreating(true);

        try {
            // lets get everything done we need to
            // 1. Create a workplace
            // 2. Create Check employee w/ workplace
            // 3. Update the employee record with the Check employee ID

            // 1. Create a workplace
            const workplaceAddress: Address = residence;

            const workplacePayload: Omit<CheckWorkplace, "id"> = {
                name: "Primary Workplace",
                address: workplaceAddress,
                company: checkCompanyId,
                active: true,
                metadata: {},
            };

            const apiResponseWorkplace = await apiCheckCreateWorkplace(checkCompanyId, workplacePayload);
            const checkWorkplace = apiResponseWorkplace?.data;
            setCheckResponseError(apiResponseWorkplace?.checkResponseError);

            if (!apiResponseWorkplace || isCheckError(apiResponseWorkplace) || !checkWorkplace) {
                toast("Error creating workplace", toastFail);
                return;
            }

            // Create Check employee
            const email = employeeRecord.workEmail || "";

            const dob = convertDateToCheckYYYYMMDD(new Date(birthDate));
            const startDateCheck = convertDateToCheckYYYYMMDD(new Date(startDate));

            // 2. Create Check employee
            const checkEmployeePayload: Partial<CheckEmployee> = {
                first_name: firstName,
                middle_name: middleName || null,
                last_name: lastName,
                email,
                residence,
                company: checkCompanyId,                
                start_date: startDateCheck,
                dob,
                workplaces: [checkWorkplace.id],
                primary_workplace: checkWorkplace.id,
                payment_method_preference: "direct_deposit",
                // ssn: ssnDigits,
            };

            const apiResponseEmployee = await apiCheckCreateEmployee(checkCompanyId, checkEmployeePayload);
            const newCheckEmployee = apiResponseEmployee?.data;
            setCheckResponseError(apiResponseEmployee?.checkResponseError);

            if (!apiResponseEmployee || isCheckError(apiResponseEmployee) || !newCheckEmployee) {
                toast("Error creating extended employee", toastFail);
                return;
            }

            const updatedEmployeeRecord = await apiUpdateEmployeeRecord({
                ...employeeRecord,
                checkEmployeeId: newCheckEmployee.id,
            });
            
            if (!updatedEmployeeRecord) {
                toast("Error updating employee record", toastFail);
                return;
            }

            toast("Employee enrolled in payroll", toastSuccess);

            // Notify parent component of the created check employee
            if (onCheckEmployeeCreated) {
                onCheckEmployeeCreated(newCheckEmployee);
            }

        } catch (error) {
            console.error("[AppEmployeeRecordPayrollEmployee] Error creating Check employee:", error);
            toast("Error creating Check employee", toastFail);
        } finally {
            setCreating(false);
        }
    };

    if (!employeeRecord) {
        return (
            <div className="app-employee-record-payroll">
                <Alert variant="warning">
                    <AlertDescription>
                        No employee record found.
                    </AlertDescription>
                </Alert>
            </div>
        );
    }


    // Bank account selection options
    const bankAccountOptions: GenericSelectOption[] = bankAccounts.map((account) => ({
        displayName: `${getInstitutionName(account)} - ${getAccountNumber(account)}`,
        value: account.id,
    }));

    // // Format address for display
    // const formatAddress = (address: Address): string => {
    //     const parts: string[] = [];
    //     if (address.line1) parts.push(address.line1);
    //     if (address.line2) parts.push(address.line2);
    //     const cityStateZip = [address.city, address.state, address.postal_code].filter(Boolean).join(", ");
    //     if (cityStateZip) parts.push(cityStateZip);
    //     if (address.country && address.country !== "US") parts.push(address.country);
    //     return parts.join("\n");
    // };

    // Check if save button should be enabled

    const canSave = (): boolean => {
        return !!(
            residence &&
            // bankAccounts.length > 0 &&
            isValidSSN(ssn)
        );
    };


    const handleSave = async () => {
        if (!canSave()) {
            toast("Please ensure residence address, bank account, and SSN are all provided", toastFail);
            return;
        }

        if (!checkEmployee) {
            // Enroll scenario - create check employee
            await handleCreateCheckEmployee();
        } else if (hasChanges) {
            // Validate SSN format before updating
            if (!isValidSSN(ssn.trim())) {
                toast("Please enter a valid Social Security Number (XXX-XX-XXXX)", toastFail);
                return;
            }
            // get only digits from ssn
            const ssnDigits = ssn.replace(/\D/g, "");
            if (ssnDigits.length !== 9) {
                toast("SSN must be exactly 9 digits", toastFail);
                return;
            }
            const apiResponse = await apiCheckUpdateEmployeeSSN(checkEmployee.id, ssnDigits);
            const result = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !result) {
                toast("Error updating SSN", toastFail);
                return;
            }

            toast("SSN updated successfully", toastSuccess);
            setHasChanges(false);
            if (onCheckEmployeeUpdated) {
                onCheckEmployeeUpdated();
            }

        }
    };

    // this only receives required parameters and tries to submit it
    const handleSubmitCheckForm = async (formId: string, parameters: Array<{ name: string; value: string }>) => {
        if (!checkEmployee?.id || submittingForm) {
            return;
        }

        setSubmittingForm(true);
        try {
            const apiResponse = await apiCheckSubmitEmployeeForm(checkEmployee.id, formId, parameters);
            const result = apiResponse?.data;
            setCheckResponseError(apiResponse?.checkResponseError);

            if (!apiResponse || isCheckError(apiResponse) || !result) {
                toast("Error submitting form", toastFail);
                return;
            }

            toast("Check form submitted successfully", toastSuccess);

            // reload forms to reflect the new status
            await loadForms();
            // Close the modal after successful submission
            handleCloseFormModal();
        } finally {
            setSubmittingForm(false);
        }
    }

    
    const handleSSNChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatSSN(e.target.value);
        setSSN(formatted);
        // Track changes for update scenario
        if (checkEmployee && formatted !== originalSSN) {
            setHasChanges(true);
        }
    };

    const hasBasics : boolean = checkEmployee &&
        checkEmployee.ssn_last_four !== null &&
        checkEmployee.bank_accounts.length > 0 &&
        checkEmployee.workplaces.length > 0 ? true : false;

    console.log("[AppEmployeeRecordPayrollEmployee] hasBasics: ", hasBasics);
    console.log("[AppEmployeeRecordPayrollEmployee] employeeRecord: ", employeeRecord);
    console.log("[AppEmployeeRecordPayrollEmployee] checkEmployee: ", checkEmployee);
    // console.log("[AppEmployeeRecordPayrollEmployee] address: ", erGetCPValue(employeeRecord, "system-address"));
    // console.log("[AppEmployeeRecordPayrollEmployee] residence: ", residence);


    return (
        <div className="app-employee-record-payroll">
            {/* Status component - shows at top if there's anything that needs attention
            {checkEmployee && checkEmployee.onboard?.status !== "completed" && (
                <AppCheckEmployeeStatus 
                    checkEmployee={checkEmployee}
                    allowCreate={false}
                    allowWarning={hasBasics}
                    handleCreateEmployee={() => {}}
                    onViewOnboarding={() => setShowOnboardingStatusModal(true)}
                />
            )} */}

            {/* Create component - shows when no checkEmployee */}
            <AppCheckEmployeeStatus 
                checkEmployee={checkEmployee}
                allowCreate={true}
                hasBasics={hasBasics}
                handleCreateEmployee={handleCreateCheckEmployee}
                onViewOnboarding={() => setShowOnboardingStatusModal(true)}
            />
        
            {checkResponseError && 
                <AppCheckErrorMessage
                    checkResponseError={checkResponseError}
                />
            }

            {/* Bank Account Form */}
            {showBankAccountForm && checkEmployee && 
                <AppPayBankAccount
                    bankAccount={editingBankAccount}
                    checkEmployeeId={checkEmployee.id}
                    onClose={() => {
                        setShowBankAccountForm(false);
                        setEditingBankAccount(null);
                    }}
                    onUpdate={handleUpdateCheckBankAccount}
                    onCreate={handleCreateCheckBankAccount}
                    onError={setCheckResponseError}
                />
            }

            {/* Main content - only show when not showing bank account form */}
            {!showBankAccountForm && checkEmployee && (
                <>
                    {/* SSN Section */}
                    <div style={{ marginBottom: "24px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <Label
                                invalid={!!ssn && ssn.trim().length > 0 && !isValidSSN(ssn.trim())}
                                invalidReason="Please enter a valid Social Security Number (XXX-XX-XXXX)"
                            >
                                Social Security Number
                            </Label>
                            <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                                <div className="app-custom-property-value" style={{ flex: 1 }}>
                                    <Input
                                        type="text"
                                        placeholder={
                                            checkEmployee?.ssn_last_four && !ssnInputFocused
                                                ? `***-**-${checkEmployee.ssn_last_four}`
                                                : "XXX-XX-XXXX"
                                        }
                                        value={ssn}
                                        onChange={handleSSNChange}
                                        onFocus={() => setSSNInputFocused(true)}
                                        onBlur={() => setSSNInputFocused(false)}
                                        maxLength={11}
                                    />
                                </div>
                                <Button
                                    label="Update"
                                    variant="primary"
                                    onClick={handleSave}
                                    disabled={creating || loadingBankAccounts || !canSave() || !hasChanges}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Bank Accounts Section */}
                    <div style={{ marginBottom: "24px" }}>
                        <div className="app-employee-payroll-bank-accounts-header">
                            <h3>Bank Accounts</h3>
                            <ActionLink
                                label="Add Bank Account"
                                leftIcon={<Plus size={16} />}
                                onClick={() => {
                                    setEditingBankAccount(null);
                                    setShowBankAccountForm(true);
                                }}
                            />
                        </div>

                        {loadingBankAccounts ? (
                            <div className="app-employee-payroll-loading">
                                Loading bank accounts...
                            </div>
                        ) : bankAccounts.length === 0 ? (
                            <Alert variant="warning" className="app-pay-configuration-alert">
                                <AlertDescription>
                                    No bank accounts found. Click "Add Bank Account" to create one.
                                </AlertDescription>
                            </Alert>
                        ) : (
                            <>
                                {bankAccounts.length > 1 && (
                                    <div className="app-employee-payroll-bank-account-select" style={{ marginBottom: "16px" }}>
                                        <div className="app-custom-property-label">
                                            Select Bank Account
                                        </div>
                                        <div className="app-custom-property-value">
                                            <GenericSelect
                                                options={bankAccountOptions}
                                                selected={bankAccountOptions.find(opt => opt.value === selectedBankAccountId)}
                                                onChange={(value: any) => setSelectedBankAccountId(value as string)}
                                                maxHeight="200px"
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="app-pay-bank-accounts-table-container">
                                    <table className="app-pay-bank-accounts-table">
                                        <thead>
                                            <tr>
                                                <th>Institution</th>
                                                <th>Account Number</th>
                                                <th>Type</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {bankAccounts.map((bankAccount) => (
                                                <tr 
                                                    key={bankAccount.id} 
                                                    className={`app-pay-bank-accounts-row ${selectedBankAccountId === bankAccount.id ? 'app-pay-bank-accounts-row-selected' : ''}`}
                                                >
                                                    <td className="app-pay-bank-accounts-institution-cell">
                                                        <strong>{getInstitutionName(bankAccount)}</strong>
                                                    </td>
                                                    <td className="app-pay-bank-accounts-account-cell">
                                                        {getAccountNumber(bankAccount)}
                                                    </td>
                                                    <td className="app-pay-bank-accounts-type-cell">
                                                        {getSubtype(bankAccount)}
                                                    </td>
                                                    <td className="app-pay-bank-accounts-status-cell">
                                                        {formatStatus(bankAccount.status)}
                                                    </td>
                                                    <td className="app-pay-bank-accounts-actions-cell">
                                                        <Icon
                                                            variant="tertiary"
                                                            size="md"
                                                            icon={<Edit size={16} />}
                                                            onClick={() => handleEditClick(bankAccount)}
                                                            title={isRawType(bankAccount) ? "Edit" : "You can not edit Plaid bank accounts"}
                                                            disabled={!isRawType(bankAccount)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Tax Forms Section */}
                    {checkEmployee.onboard?.withholdings && checkEmployee.onboard.withholdings.length > 0 && (
                        <div style={{ marginTop: "32px" }}>
                            <h3 style={{ marginBottom: "24px" }}>Tax Forms and Withholdings</h3>
                            {loadingForms ? (
                                <div className="app-employee-payroll-loading">
                                    Loading required withholding forms...
                                </div>
                            ) : checkFormStatuses.length > 0 ? (
                                <div className="app-pay-bank-accounts-table-container">
                                    <table className="app-pay-bank-accounts-table">
                                        <thead>
                                            <tr>
                                                <th>Description</th>
                                                <th>Status</th>
                                                <th>Submitted</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {checkFormStatuses.map((formStatus: CheckFormStatus) => {
                                                const isSubmitted = !!formStatus.submitted_at;
                                                return (
                                                    <tr 
                                                        key={formStatus.form.id}
                                                        className="app-pay-bank-accounts-row"
                                                    >
                                                        <td>
                                                            <strong>{formStatus.form.description}</strong>
                                                        </td>
                                                        <td>
                                                            <span className={isSubmitted ? "blocking-badge non-blocking" : "blocking-badge blocking"}>
                                                                {isSubmitted ? "Submitted" : "Not Submitted"}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {isSubmitted ? formatDateTime(formStatus.submitted_at) : "-"}
                                                        </td>
                                                        <td>
                                                            <Button
                                                                label={isSubmitted ? "Update" : "Submit"}
                                                                variant="secondary"
                                                                onClick={() => handleOpenForm(formStatus)}
                                                                disabled={submittingForm || loadingFormDetails}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <Alert variant="default" className="app-pay-configuration-alert">
                                    <AlertDescription>
                                        No withholding forms found.
                                    </AlertDescription>
                                </Alert>
                            )}
                        </div>
                    )}

                </>
            )}

            {checkEmployee && (
                <ModalComponentWrapper
                    isOpen={showOnboardingStatusModal}
                    onClose={() => setShowOnboardingStatusModal(false)}
                    style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
                >
                    <div style={{ padding: "24px" }}>
                        <AppPayEmployeeOnboardingStatus 
                            checkEmployee={checkEmployee}
                            showInModal={true}
                        />
                    </div>
                </ModalComponentWrapper>
            )}

            {/* Form Modal */}
            {checkEmployee && (
                <ModalComponentWrapper
                    isOpen={showFormModal}
                    onClose={handleCloseFormModal}
                    style={{ width: "90%", maxWidth: "900px", maxHeight: "90vh", overflow: "auto" }}
                >
                    <div style={{ padding: "24px" }}>
                        {loadingFormDetails ? (
                            <div className="app-employee-payroll-loading">
                                Loading form details...
                            </div>
                        ) : selectedForm && selectedFormStatus ? (
                            <AppEmployeeRecordCheckForm
                                checkForm={selectedForm}
                                showTitle={true}
                                disabled={submittingForm}
                                isSubmitted={!!selectedFormStatus.submitted_at}
                                onSubmit={(parameters) => {
                                    handleSubmitCheckForm(selectedForm.id, parameters);
                                }}
                            />
                        ) : (
                            <Alert variant="warning">
                                <AlertDescription>
                                    Form not found.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                </ModalComponentWrapper>
            )}
        </div>
    );
};

